import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCryptoCurrencies,
  getNowPaymentsPublicConfig,
  createCryptoCheckout,
  estimateCrypto,
} from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Bitcoin, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Pretty labels per currency code
const CRYPTO_LABELS = {
  btc: { name: 'Bitcoin', symbol: 'BTC', network: '', emoji: '₿' },
  eth: { name: 'Ethereum', symbol: 'ETH', network: '', emoji: 'Ξ' },
  usdttrc20: { name: 'Tether', symbol: 'USDT', network: 'TRC20', emoji: '$' },
  usdterc20: { name: 'Tether', symbol: 'USDT', network: 'ERC20', emoji: '$' },
  usdc: { name: 'USD Coin', symbol: 'USDC', network: 'ETH', emoji: '$' },
  sol: { name: 'Solana', symbol: 'SOL', network: '', emoji: '◎' },
  bnbbsc: { name: 'BNB', symbol: 'BNB', network: 'BSC', emoji: 'B' },
  ltc: { name: 'Litecoin', symbol: 'LTC', network: '', emoji: 'Ł' },
  doge: { name: 'Dogecoin', symbol: 'DOGE', network: '', emoji: 'Ð' },
  xrp: { name: 'XRP', symbol: 'XRP', network: '', emoji: 'X' },
  matic: { name: 'Polygon', symbol: 'MATIC', network: '', emoji: 'M' },
  trx: { name: 'Tron', symbol: 'TRX', network: '', emoji: 'T' },
  dai: { name: 'DAI', symbol: 'DAI', network: 'ETH', emoji: '$' },
  avax: { name: 'Avalanche', symbol: 'AVAX', network: '', emoji: 'A' },
  shib: { name: 'Shiba Inu', symbol: 'SHIB', network: '', emoji: 'S' },
};

/**
 * Drop-in component for any checkout page. Renders a "Payer en crypto" button
 * that opens a dialog where the user picks a cryptocurrency, then triggers
 * createCryptoCheckout() and navigates to /payment/crypto with the result.
 *
 * Props:
 *  - payload: object passed directly to createCryptoCheckout (without pay_currency)
 *  - amountGbp: number — display only
 *  - disabled / className
 */
export default function PayWithCryptoButton({ payload, amountGbp = 0, disabled = false, className = '' }) {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [minAmount, setMinAmount] = useState(5);
  const [open, setOpen] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getNowPaymentsPublicConfig().then((r) => {
      if (cancelled) return;
      setEnabled(!!r.data?.enabled);
      setMinAmount(r.data?.min_amount_gbp ?? 5);
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  // Refresh the crypto estimate whenever the selected currency changes —
  // shows the user the EXACT amount they'll be asked to send before they
  // confirm. Includes the NOWPayments service + network fees.
  useEffect(() => {
    if (!open || !selected || !amountGbp) return;
    let cancelled = false;
    setEstimating(true);
    estimateCrypto(amountGbp, selected, 'gbp')
      .then((res) => { if (!cancelled) setEstimate(res.data); })
      .catch(() => { if (!cancelled) setEstimate(null); })
      .finally(() => { if (!cancelled) setEstimating(false); });
    return () => { cancelled = true; };
  }, [open, selected, amountGbp]);

  const openDialog = async () => {
    if (amountGbp < minAmount) {
      toast.error(`Montant minimum crypto : £${minAmount}`);
      return;
    }
    setOpen(true);
    if (currencies.length === 0) {
      setLoading(true);
      try {
        const res = await getCryptoCurrencies();
        const list = res.data?.currencies || [];
        setCurrencies(list);
        if (list.length > 0) setSelected(list[0].currency);
      } catch {
        toast.error('Erreur de chargement des cryptos');
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePay = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const body = { ...payload, pay_currency: selected, origin_url: window.location.origin };
      const res = await createCryptoCheckout(body);
      // Navigate to the payment page with state
      navigate('/payment/crypto', { state: { crypto: res.data } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de paiement crypto');
    } finally {
      setSubmitting(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      <Button
        type="button"
        onClick={openDialog}
        disabled={disabled}
        className={`gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md ${className}`}
        data-testid="pay-with-crypto-btn"
      >
        <Bitcoin className="w-4 h-4" /> Payer en crypto
      </Button>

      <Dialog open={open} onOpenChange={(o) => !submitting && setOpen(o)}>
        <DialogContent className="bg-cream max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bitcoin className="w-5 h-5 text-gold-dark" /> Payer en cryptomonnaie
            </DialogTitle>
            <DialogDescription>
              Choisissez la crypto avec laquelle vous voulez payer £{Number(amountGbp).toFixed(2)}.
              Conversion automatique et instantanée.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-gold" />
            </div>
          ) : currencies.length === 0 ? (
            <div className="py-6 text-center text-rolex-green/55">
              <p className="text-sm">Aucune crypto disponible pour le moment.</p>
              <p className="text-xs mt-1">L'admin doit activer au moins une crypto.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {currencies.map((c) => {
                const meta = CRYPTO_LABELS[c.currency] || { name: c.currency.toUpperCase(), symbol: c.currency.toUpperCase(), network: '', emoji: '?' };
                const active = selected === c.currency;
                return (
                  <button
                    key={c.currency}
                    type="button"
                    onClick={() => setSelected(c.currency)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${active ? 'border-orange-500 bg-orange-50 shadow-md' : 'border-stone-200 bg-white hover:border-orange-300'}`}
                    data-testid={`crypto-option-${c.currency}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-base ${active ? 'bg-orange-500 text-white' : 'bg-stone-100 text-rolex-green'}`}>
                      {meta.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-rolex-green text-sm">{meta.symbol}</p>
                        {meta.network && <Badge variant="outline" className="text-[9px] py-0">{meta.network}</Badge>}
                      </div>
                      <p className="text-[11px] text-rolex-green/65">{meta.name}</p>
                    </div>
                    {active && <CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Fee preview — show what the user will REALLY pay ── */}
          {selected && estimate && !loading && (
            <div className="rounded-xl border-2 border-orange-300 bg-orange-50 p-3 my-3 text-xs space-y-1">
              <p className="flex items-start gap-2 font-bold text-orange-900">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                Montant approximatif à envoyer
              </p>
              <div className="flex justify-between text-rolex-green/85">
                <span>Prix du produit</span>
                <span className="font-mono font-semibold">£{Number(amountGbp).toFixed(2)}</span>
              </div>
              <div className="border-t border-orange-200 pt-1.5 mt-1.5 flex justify-between text-base font-bold text-orange-900">
                <span>≈ Total estimé en {(selected || '').toUpperCase()}</span>
                <span className="font-mono">{Number(estimate.estimated_amount).toFixed(2)}</span>
              </div>
              <p className="text-[10px] text-orange-700/85 italic mt-1">
                ⚠️ Montant final légèrement supérieur (≈ +3-5%) car frais réseau crypto inclus. Vous verrez le montant exact à la prochaine étape avant d'envoyer.
              </p>
            </div>
          )}
          {estimating && (
            <div className="text-center text-xs text-rolex-green/55 my-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Calcul du montant exact…
            </div>
          )}

          <DialogFooter className="gap-2 flex flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button
              onClick={handlePay}
              disabled={!selected || submitting || estimating}
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white gap-2"
              data-testid="crypto-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bitcoin className="w-4 h-4" />}
              Continuer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
